import React, { useCallback, useMemo, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES } from 'components/bottom-service-mode/hooks/use-bottom-service-details';
import { postOrderTimeout } from 'components/bottom-service-mode/utils';
import { DeliveryStatus, RbiOrderStatus } from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import { useUserOrders } from 'hooks/use-user-orders';
import { serviceModeToIntlId } from 'pages/store-locator/new-ui/store-card/hooks/use-service-mode-text';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useReversedUIContext } from 'state/reversed-ui';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import CtaButton from '../actions/cta-button';
import { CurbsideConfirmationModal } from '../curbside-confirmation-modal';
import { ITopServiceModeDetail, TopServiceVariants } from '../types';

export const useTopServiceModeDetails = (): ITopServiceModeDetail => {
  const { serviceMode, deliveryAddress, curbsidePickupOrderId, curbsidePickupOrderTimePlaced } =
    useOrderContext();
  const { store, noStoreSelected } = useStoreContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const isLocatorUIRefreshEnabled = useFlag(LaunchDarklyFlag.ENABLE_LOCATOR_UI_REFRESH);
  const [curbsideConfirmationModalOpen, setCurbsideConfirmationModalOpen] =
    useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { logEvent } = useMParticleContext();
  const { reversedUI } = useReversedUIContext();
  const { isAuthenticated } = useAuthContext();
  const isAuth = isAuthenticated();

  const iconColor = reversedUI ? 'icon-reversed' : 'icon-default';

  const navigate = useNavigate();
  const [ConfirmationModal, openConfirmationModal] = useDialogModal({
    showCancel: true,
    onConfirm: () => navigate(routes.serviceMode),
    modalAppearanceEventMessage: 'Service Mode/Address Confirmation',
  });

  const { data, refetch } = useUserOrders({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.UPDATE_ERROR, // not a terminal status, the order could continue
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
  });

  const isCurrentDeliveryOrder = useMemo(() => {
    const deliveryOrder = data?.userOrders?.orders?.[0];
    const orderStatus = deliveryOrder?.delivery?.status;
    const failedOrderStatus = [
      DeliveryStatus.ORDER_CANCELLED,
      DeliveryStatus.ORDER_ABANDONED,
      DeliveryStatus.ORDER_DROPPED_OFF,
    ];

    if (!deliveryOrder || !orderStatus || failedOrderStatus.includes(orderStatus)) {
      return null;
    }
    return deliveryOrder;
  }, [data]);

  // @TODO: Currently we have duplicated logic for recentOrders between FAB Button, Top service Mode and Service Notification
  // We must find a way to avoid this.
  const isPreConfirmCurbside = useCallback(() => {
    return (
      curbsidePickupOrderId &&
      postOrderTimeout(
        curbsidePickupOrderTimePlaced,
        PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES
      )
    );
  }, [curbsidePickupOrderId, curbsidePickupOrderTimePlaced]);

  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
    });

    if (isPreConfirmCurbside() && isAuth) {
      return setCurbsideConfirmationModalOpen(true);
    }

    if (!isLocatorUIRefreshEnabled && !noStoreSelected && serviceMode) {
      return openConfirmationModal();
    }
    navigate(routes.serviceMode);
  }, [
    isAuth,
    isLocatorUIRefreshEnabled,
    isPreConfirmCurbside,
    logEvent,
    navigate,
    noStoreSelected,
    openConfirmationModal,
    serviceMode,
  ]);

  const handleNavigateToOrderConfirmation = (rbiOrderId: string) => {
    logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
      'Order Id': rbiOrderId,
    });
    navigate(`${routes.orderConfirmation}/${rbiOrderId}`, {
      state: { showToast: true },
    });
  };

  const getCtaButton = () => {
    if (isPreConfirmCurbside()) {
      return (
        <CtaButton
          text="confirmArrival"
          cta={() => handleNavigateToOrderConfirmation(curbsidePickupOrderId)}
        />
      );
    } else if (isCurrentDeliveryOrder) {
      return (
        <CtaButton
          text="trackOrder"
          cta={() => handleNavigateToOrderConfirmation(isCurrentDeliveryOrder.rbiOrderId)}
        />
      );
    }
    return <CtaButton text="change" cta={handleServiceModeChange} />;
  };

  const resultBase: ITopServiceModeDetail = {
    heading: formatMessage({ id: 'forItemAvailability' }),
    details: null,
    icon: <Icon icon="location" color={iconColor} width="20px" height="20px" aria-hidden />,
    button: null,
    onTopServiceClick: handleServiceModeChange,
    variant: TopServiceVariants.DEFAULT,
    ConfirmationModal,
    CurbsideConfirmationModal,
    curbsideConfirmationModalOpen,
    setCurbsideConfirmationModalOpen,
    refetchUserOrder: refetch,
  };

  const CtaBtn = getCtaButton();

  if (store._id) {
    if ((!store.hasMobileOrdering || !enableOrdering) && store.physicalAddress?.address1) {
      return {
        ...resultBase,
        button: CtaBtn,
        heading: formatMessage({ id: 'yourSelectedStore' }),
        details: store.physicalAddress.address1,
        icon: <Icon icon="restaurant" color={iconColor} width="20px" height="20px" aria-hidden />,
      };
    }
    switch (serviceMode) {
      case ServiceMode.DELIVERY:
        if (deliveryAddress) {
          return {
            ...resultBase,
            button: CtaBtn,
            heading: formatMessage({ id: 'delivery' }),
            details: deliveryAddress.addressLine1,
            icon: <Icon icon="delivery" color={iconColor} width="20px" height="20px" aria-hidden />,
          };
        }
        break;
      case ServiceMode.CURBSIDE:
        if (store.physicalAddress?.address1) {
          return {
            ...resultBase,
            button: CtaBtn,
            heading: formatMessage({ id: 'curbside' }),
            details: store.physicalAddress.address1,
            icon: (
              <Icon icon="restaurant" color={iconColor} width="20px" height="20px" aria-hidden />
            ),
          };
        }
        break;
      case ServiceMode.TAKEOUT:
      case ServiceMode.DRIVE_THRU:
      case ServiceMode.EAT_IN:
      case ServiceMode.TABLE_SERVICE:
        if (store.physicalAddress?.address1) {
          return {
            ...resultBase,
            button: CtaBtn,
            heading: formatMessage({ id: serviceModeToIntlId[serviceMode] }),
            details: store.physicalAddress.address1,
            icon: (
              <Icon icon="restaurant" color={iconColor} width="20px" height="20px" aria-hidden />
            ),
          };
        }
        break;
      default:
    }
  }
  return {
    ...resultBase,
    variant: TopServiceVariants.NO_STORE,
  };
};
